import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { LoginAuthWithCredentialsResponse } from '../../../models/login-remember-me.interfaces';
import {
  ILoginUserNamePasswordInput,
  LoginOAuthParams
} from '../../../models/oauth.interfaces';
import {
  AuthAccessTokenDetails,
  SwapAuthForAccessTokens
} from '../../../models/tokens.interfaces';
import { SwapAccessTokenService } from '../../swap-access-token-service/swap-access-token-service';
import { LoginAuthorizationService } from '../login-remember-me-adapter-service/login-authorization/login-authorization-service';
import { LoginPostWithFingerPrintService } from '../login-remember-me-adapter-service/login-post-with-fingerprint/login-post-with-fingerprint-service';
import {
  checkRequestInput,
  checkRequestIsDefined,
  isCompletedStatus,
  isUserNamePasswordRequired,
  returnAuthorizationResponseCode
} from '../login-remember-me-utils-service/initiate-login-utils';
import { LoginRememberMeErrorUtilService } from '../login-remember-me-utils-service/login-remember-me-error-util.service';
import { LoginRememberMeUtilsService } from '../login-remember-me-utils-service/login-remember-me-utils.service';
import { PostWithCredentialsFacadeService } from './post-with-credentials-facade.service';

@Injectable({
  providedIn: 'root'
})
export class InitiateLoginFacadeExtendedHelperService {
  retryCount = 0;
  loginAuthorization = inject(LoginAuthorizationService);
  loginPostWithFingerPrint = inject(LoginPostWithFingerPrintService);
  loginRememberMeUtils = inject(LoginRememberMeUtilsService);
  postWithCredentialsFacade = inject(PostWithCredentialsFacadeService);
  loginRememberMeErrorUtil = inject(LoginRememberMeErrorUtilService);
  swapAccessToken = inject(SwapAccessTokenService);

  private isAccessTokenResponse(
    response:
      | LoginAuthWithCredentialsResponse
      | SwapAuthForAccessTokens
      | AuthAccessTokenDetails
      | string
      | boolean
  ): response is AuthAccessTokenDetails {
    return (
      typeof response === 'object' &&
      response !== null &&
      'access_token' in response
    );
  }

  handleCredentialsResponse(
    response: LoginAuthWithCredentialsResponse | SwapAuthForAccessTokens,
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput | undefined
  ): Observable<
    | LoginAuthWithCredentialsResponse
    | SwapAuthForAccessTokens
    | AuthAccessTokenDetails
    | string
    | boolean
  > {
    if (this.isAccessTokenResponse(response)) {
      return of(response);
    } else if (
      isUserNamePasswordRequired(
        response as LoginAuthWithCredentialsResponse,
        loginOAuthParams
      )
    ) {
      return checkRequestInput(
        loginOAuthParams,
        response as LoginAuthWithCredentialsResponse,
        this.postWithCredentialsFacade,
        checkRequestIsDefined(requestInput)
      );
    } else if (
      isCompletedStatus(response as SwapAuthForAccessTokens, loginOAuthParams)
    ) {
      return this.loginRememberMeUtils.swapAuthCodeForToken(
        loginOAuthParams,
        returnAuthorizationResponseCode(response as SwapAuthForAccessTokens)
      );
    } else {
      return throwError(response);
    }
  }
}
